import { ContentObserver } from '@angular/cdk/observers';
import { isPlatformBrowser } from '@angular/common';
import type {
  AfterViewInit,
  ComponentRef,
  EmbeddedViewRef,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  Type,
} from '@angular/core';
import {
  Directive,
  Inject,
  Input,
  PLATFORM_ID,
  Renderer2,
  SimpleChange,
  SkipSelf,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';
import { LinkshimComponent } from '@freelancer/components/linkshim';
import { LinkColor, LinkComponent, LinkHoverColor } from '@freelancer/ui/link';
import { TextSize } from '@freelancer/ui/text';
import type { Match } from 'autolinker';
import Autolinker from 'autolinker';
import type { Subscription } from 'rxjs';

type LinkMatch = Match & { getUrl(): string; protocolRelativeMatch: boolean };

interface LinkProperties {
  readonly color: LinkColor;
  readonly hoverColor: LinkHoverColor;
  readonly link: string;
  readonly newTab: boolean;
  readonly size: TextSize;
}

@Directive({ standalone: true, selector: '[flAutoLink]' })
export class AutoLinkDirective
  implements AfterViewInit, OnInit, OnChanges, OnDestroy
{
  /**
   * The following input will limit the matching urls
   * due to performance issue.
   */
  private readonly MAX_ALLOWED_LINKS = 200;

  /**
   * Default true, true will change the linkification output to `fl-linkshim`
   * while false will change the output to `fl-link`
   */
  private _flAutoLinkShim = true;

  /**
   * Introducing _flAutoLinkShim with setter because
   * when using directive like following <ng-container *flAutoLink>
   * then it equals '' and '' is not of type boolean.
   */

  @Input()
  set flAutoLink(autoLink: boolean | '') {
    this._flAutoLinkShim = autoLink === '' ? true : autoLink;
  }

  @Input() flAutoLinkHoverColor = LinkHoverColor.DEFAULT;
  @Input() flAutoLinkColor = LinkColor.DEFAULT;

  private componentInstances: ComponentRef<
    LinkshimComponent | LinkComponent
  >[] = [];
  private createdTextNodes: readonly Text[] = [];
  private embeddedViewRef: EmbeddedViewRef<any>;
  private mutationSub?: Subscription;

  hasBeenLinkifiedBefore = false;
  private autolinker = new Autolinker({
    urls: true,
    email: false,
    phone: false,
  });

  constructor(
    private containerRef: ViewContainerRef,
    private contentObserver: ContentObserver,
    private templateRef: TemplateRef<any>,
    @SkipSelf() protected renderer: Renderer2,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {}

  ngOnInit(): void {
    this.embeddedViewRef = this.containerRef.createEmbeddedView(
      this.templateRef,
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes && this.componentInstances.length > 0) {
      // Manually trigger ngOnChanges on each Link component
      this.componentInstances.forEach(instance =>
        this.notifyInputChanges(instance, {
          color: this.flAutoLinkColor,
          hoverColor: this.flAutoLinkHoverColor,
        }),
      );
    }
  }

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      const rootNode = this.embeddedViewRef.rootNodes[0];

      this.linkifyContent(rootNode);
      this.embeddedViewRef.markForCheck();

      // listen for changes of content
      this.mutationSub = this.contentObserver
        .observe(rootNode)
        .subscribe(mutationRecords => {
          const linkified = mutationRecords.some(mutationRecord => {
            const addedNode = mutationRecord.addedNodes[0];
            const removedNode = mutationRecord.removedNodes[0];
            return (
              // Once `fl-linkshim` added don't run linkification again
              addedNode?.nodeName === 'FL-LINKSHIM' ||
              // Once a button added don't run linkification again
              addedNode instanceof HTMLButtonElement ||
              // Once a button removed don't run linkification again
              removedNode instanceof HTMLButtonElement
            );
          });

          if (!linkified) {
            this.linkifyContent(rootNode);
            this.embeddedViewRef.markForCheck();
          }
        });
    }
  }

  ngOnDestroy(): void {
    this.embeddedViewRef.destroy();
    this.destroyLinks();
    if (this.mutationSub) {
      this.mutationSub.unsubscribe();
    }
  }

  linkifyContent(root: HTMLElement): void {
    if (this.hasBeenLinkifiedBefore) {
      this.destroyLinks(); // clean up previous run
    } else {
      this.hasBeenLinkifiedBefore = true; // now this is the first run
    }

    const textNodes = this.findTextNodes(root);
    textNodes.forEach(textNode => this.linkTransform(textNode));
  }

  private destroyLinks(): void {
    this.componentInstances.forEach(componentInstance => {
      componentInstance.destroy();
      componentInstance.location.nativeElement.remove();
    });
    this.componentInstances = [];
    this.createdTextNodes.forEach(createdTextNode => createdTextNode.remove());
    this.createdTextNodes = [];
  }

  /**
   * Find every url in text and replace them to `fl-link` or `fl-linkshim`
   *
   * @param textNode
   */
  private linkTransform(textNode: Text): void {
    const { parentNode } = textNode;
    if (parentNode === null) {
      // prevent nodes that does not have any parent
      return;
    }

    const value = textNode.wholeText;
    const matches = this.autolinker.parse(value).filter(this.checkUrl);

    if (matches.length === 0) {
      // prevent to re-create the dom structure
      return;
    }

    // tldr of linkify logic: clear node's content, then populate it with 1. original non-link text and 2. newly transformed linkified text in the correct positions
    // this way angular can update the text node, and our MutationObserver will detect changes and re-linkify as needed.
    textNode.textContent = ''; // <- emptying the nodes content.
    let lastElement: Node = textNode;

    let lastOffset = 0;

    // now we populate with transformed text.
    matches.forEach((match, index) => {
      if (index >= this.MAX_ALLOWED_LINKS) {
        return;
      }

      // 1. Insert plain text before the link occurrence.
      const chunk = value.substring(lastOffset, match.getOffset());
      if (chunk) {
        const txt = this.renderer.createText(chunk);
        this.createdTextNodes = [...this.createdTextNodes, txt];
        parentNode.insertBefore(txt, lastElement.nextSibling);
        lastElement = txt;
      }

      lastOffset = match.getOffset() + match.getMatchedText().length;

      // 2. Insert the generatedlink component for the matched URL.
      const linkEl = this.renderLinkComponent(
        match.getMatchedText(),
        parentNode,
        {
          color: this.flAutoLinkColor,
          hoverColor: this.flAutoLinkHoverColor,
          link: `${match.protocolRelativeMatch ? 'https:' : ''}${match.getUrl()}`,
          newTab: true,
          size: TextSize.INHERIT,
        },
      );
      parentNode.insertBefore(linkEl, lastElement.nextSibling);
      lastElement = linkEl;
    });

    // 3. Insert any trailing plain text after the link.
    if (lastOffset < value.length) {
      const tail = this.renderer.createText(value.substring(lastOffset));
      this.createdTextNodes = [...this.createdTextNodes, tail];
      parentNode.insertBefore(tail, lastElement.nextSibling);
    }
  }

  private checkUrl(match: Match): match is LinkMatch {
    return match.getType() === 'url';
  }

  /**
   * Create a valid `fl-link` component
   *
   * @param factory
   * @param content
   * @param parentNode
   * @param linkProps
   */
  private renderLinkComponent(
    content: string,
    parentNode: Node,
    linkProps: LinkProperties,
  ): HTMLElement {
    const type: Type<LinkshimComponent | LinkComponent> = this._flAutoLinkShim
      ? LinkshimComponent
      : LinkComponent;
    /**
     * <fl-link ...>
     *     {{ content }}
     * </fl-link>
     */
    const linkComponentRef = this.containerRef.createComponent(type, {
      index: this.containerRef.length,
      injector: this.containerRef.injector,
      projectableNodes: [[this.renderer.createText(content)]],
    });

    /**
     * Bind inputs and fire component ngOnChanges
     */
    this.notifyInputChanges(linkComponentRef, linkProps);

    // Add created component into the array to detach from the container
    // and destroy it at the `ngOnDestroy` hook
    this.componentInstances.push(linkComponentRef);

    return linkComponentRef.location.nativeElement;
  }

  /**
   * Manually bind LinkshimComponent @Input values
   * and mark them for check.
   *
   * @param componentRef
   * @param componentProps
   */
  private notifyInputChanges(
    componentRef: ComponentRef<LinkshimComponent | LinkComponent>,
    componentProps: object,
  ): void {
    const { instance } = componentRef as any;
    const changes = Object.entries(componentProps).reduce(
      (map, [key, value]) => {
        instance[key] = value;

        if (instance.ngOnChanges) {
          const change = new SimpleChange(key, value, true);

          return {
            ...map,
            [key]: change,
          };
        }

        return map;
      },
      {},
    );

    /**
     * Dynamically created components do not trigger `ngOnChanges`
     * whenever input changes. The function that performs inputs
     * checks is generated during compilation based on the template.
     * Thus, we need to trigger `ngOnChanges` manually.
     * https://github.com/angular/angular/issues/9866
     */
    if (instance.ngOnChanges) {
      instance.ngOnChanges(changes);
      instance.changeDetectorRef.markForCheck();
    }

    const { changeDetectorRef } = componentRef;
    changeDetectorRef.detectChanges();
  }

  /**
   * Find text nodes.
   *
   * @param current
   */
  private findTextNodes(current: Node): Text[] {
    const nodeIterator = document.createNodeIterator(
      current,
      NodeFilter.SHOW_TEXT,
      null,
    );

    const nodes: Text[] = [];
    while (nodeIterator.nextNode()) {
      const currentNode = nodeIterator.referenceNode;
      if (this.isTextNode(currentNode)) {
        nodes.push(currentNode);
      }
    }
    return nodes;
  }

  private isTextNode(node: Node | null): node is Text {
    return node?.nodeType === Node.TEXT_NODE;
  }
}
